import { Link, useNavigate } from "react-router-dom";
import { useZoom } from "../context/ZoomContext";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuPortal,
} from "./ui/dropdown-menu";
import {
  MoreVertical, KeyRound, UserCog, History, Download, Star, ZoomIn, ZoomOut, RotateCcw,
  Printer, Languages, Cast, Save, Share2, Code2, HelpCircle, Settings,
} from "lucide-react";

export function MainMenu() {
  const navigate = useNavigate();
  const { zoom, inc, dec, reset } = useZoom();

  const print = () => {
    toast.info("Ouverture de la fenêtre d'impression…");
    setTimeout(() => window.print(), 200);
  };

  const savePage = () => {
    const blob = new Blob([document.documentElement.outerHTML], { type: "text/html" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `omnisearch-${document.title.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.html`;
    link.click();
    URL.revokeObjectURL(link.href);
    toast.success("Page enregistrée");
  };

  const share = async () => {
    const data = { title: document.title, url: window.location.href };
    if (navigator.share) {
      try { await navigator.share(data); } catch {}
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success("Lien copié dans le presse-papier");
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="main-menu-trigger"
          aria-label="Menu"
          className="p-2 rounded-full hover:bg-gray-100 transition"
        >
          <MoreVertical size={20} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72" data-testid="main-menu">
        <DropdownMenuLabel className="text-xs text-gray-500">OmniSearch</DropdownMenuLabel>
        <DropdownMenuItem asChild data-testid="menu-account">
          <Link to="/account"><UserCog className="mr-2 h-4 w-4" /> Compte Omni</Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild data-testid="menu-passwords">
          <Link to="/passwords"><KeyRound className="mr-2 h-4 w-4" /> Mots de passe & saisie automatique</Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild data-testid="menu-history">
          <Link to="/history"><History className="mr-2 h-4 w-4" /> Historique</Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild data-testid="menu-downloads">
          <Link to="/downloads"><Download className="mr-2 h-4 w-4" /> Téléchargements</Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild data-testid="menu-bookmarks">
          <Link to="/bookmarks"><Star className="mr-2 h-4 w-4" /> Favoris</Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuSub>
          <DropdownMenuSubTrigger data-testid="menu-zoom-trigger">
            <div className="flex items-center"><ZoomIn className="mr-2 h-4 w-4" /> Zoom</div>
            <span className="ml-auto text-xs font-mono text-gray-500">{zoom}%</span>
          </DropdownMenuSubTrigger>
          <DropdownMenuPortal>
            <DropdownMenuSubContent className="w-44">
              <DropdownMenuItem onClick={dec} data-testid="zoom-out">
                <ZoomOut className="mr-2 h-4 w-4" /> Réduire
              </DropdownMenuItem>
              <DropdownMenuItem onClick={inc} data-testid="zoom-in">
                <ZoomIn className="mr-2 h-4 w-4" /> Agrandir
              </DropdownMenuItem>
              <DropdownMenuItem onClick={reset} data-testid="zoom-reset">
                <RotateCcw className="mr-2 h-4 w-4" /> 100%
              </DropdownMenuItem>
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>
        <DropdownMenuItem onClick={print} data-testid="menu-print">
          <Printer className="mr-2 h-4 w-4" /> Imprimer…
        </DropdownMenuItem>
        <DropdownMenuItem asChild data-testid="menu-translate">
          <Link to="/translate"><Languages className="mr-2 h-4 w-4" /> Traduire</Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild data-testid="menu-cast">
          <Link to="/cast"><Cast className="mr-2 h-4 w-4" /> Caster…</Link>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={savePage} data-testid="menu-save">
          <Save className="mr-2 h-4 w-4" /> Enregistrer la page
        </DropdownMenuItem>
        <DropdownMenuItem onClick={share} data-testid="menu-share">
          <Share2 className="mr-2 h-4 w-4" /> Partager
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild data-testid="menu-devtools">
          <Link to="/devtools"><Code2 className="mr-2 h-4 w-4" /> Outils de développement</Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild data-testid="menu-help">
          <Link to="/help"><HelpCircle className="mr-2 h-4 w-4" /> Aide</Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild data-testid="menu-settings">
          <Link to="/settings"><Settings className="mr-2 h-4 w-4" /> Paramètres</Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
